import { useRef, useState } from 'react';
import { useRouter } from 'next/router';

// Hooks
import { PREVIOUS_STEP, useAlbumContext } from '@/utils/hooks/context/useAlbumContext';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useIsGroup from '@/utils/hooks/user/useIsGroup';

// Utils
import { post, put } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import useIntersectionObserver from '@/utils/hooks/utils/useIntersectionObserver';

// Lib
import { ALBUM, ALBUMS } from '@/lib/routes/apiEndpoints';

//libs
import Badge from '@/components/base/Badge';
import Icon from '@/components/base/Icon';
import InfoBanner from '@/components/base/InfoBanner';
// Components
import Modal from '@/components/base/Modal';
import FooterModal from '@/components/pages/albums/AlbumFunnelModal/components/FooterModal';
import { InputAlbumName } from '@/components/pages/albums/AlbumFunnelModal/components/InputAlbumName';
import InstagramMediaType from '@/components/pages/albums/AlbumFunnelModal/components/InstagramMediaType';
import AlbumManualFilters from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/ManualStep/components/AlbumManualFilters';
import PostListWrapper from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/ManualStep/components/PostListWrapper';
import StoriesListWrapper from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/ManualStep/components/StoriesListWrapper';

// Routes
import { CONTENT_ALBUM_ANALYTICS } from '@/routes/privateRoutes';

export const ManualStep = ({ dismiss, callbackUpdateAlbum }) => {
    const router = useRouter();
    const { t } = useTranslation('content');
    const refBannerInfo = useRef();
    const bannerInfoInViewPort = useIntersectionObserver(refBannerInfo, '0px');
    const [submitStatus, setSubmitStatus] = useState({
        isSubmitting: false,
        error: null
    });

    const { albumInfo, dispatch } = useAlbumContext();
    const { activeSocialProfile } = useActiveSocialProfile();
    const { activeGroup } = useActiveGroup();
    const isGroup = useIsGroup();

    const buildAlbumPayload = () => {
        const payload = {
            name: albumInfo?.name,
            type: albumInfo?.type,
            medias: albumInfo?.medias
        };

        if (isGroup) {
            payload.group_id = activeGroup.id;
            payload.criteria = { album_type: albumInfo?.criteria?.album_type || 'posts' };
        } else {
            payload.social_profile_id = activeSocialProfile?.id;
            if (albumInfo?.criteria?.album_type) {
                payload.criteria = { album_type: albumInfo.criteria.album_type };
            }
        }

        return payload;
    };

    const handleSubmitError = (e) => {
        const isUniqueNameError = e?.response?.data?.errors?.name?.includes('unique');
        const errorMessage = isUniqueNameError
            ? t('content:modal.update_album_error_name_unique')
            : t('errors:error_occured_submit_data');

        setSubmitStatus((prevState) => ({
            ...prevState,
            isSubmitting: false,
            error: errorMessage
        }));

        if (!bannerInfoInViewPort) {
            refBannerInfo?.current?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const submitManualAlbum = async () => {
        setSubmitStatus((prevState) => ({
            ...prevState,
            isSubmitting: true,
            error: null
        }));

        try {
            const dataToSend = buildAlbumPayload();

            if (albumInfo?.id) {
                await put(injectEndpointUrlParams(ALBUM, { ALBUM_ID: albumInfo.id }), dataToSend);
                await callbackUpdateAlbum?.();
                dismiss?.();
            } else {
                const { data } = await post(ALBUMS, dataToSend);
                router.push(
                    CONTENT_ALBUM_ANALYTICS.replace('[albumId]', data?.id || data?.data?.id)
                );
            }
        } catch (e) {
            handleSubmitError(e);
        }
    };

    return (
        <div className="flex max-h-[calc(100vh-100px)] min-h-[500px] w-full flex-col">
            <Modal.Body className="flex flex-1 flex-col overflow-hidden px-5 pt-12 sm:px-12">
                <div ref={refBannerInfo} />
                {submitStatus?.error ? (
                    <InfoBanner
                        className="mb-4"
                        title={t('errors:error')}
                        message={submitStatus?.error}
                        onClose={() => {
                            setSubmitStatus((prevState) => ({
                                ...prevState,
                                error: null
                            }));
                        }}
                    />
                ) : null}
                <div className="mb-8 flex items-center gap-3">
                    {!albumInfo?.id && (
                        <button
                            onClick={() => dispatch({ type: PREVIOUS_STEP })}
                            disabled={submitStatus?.isSubmitting}
                            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-icoGray-400 text-icoGray-400 transition-colors hover:bg-icoGray-100 dark:border-icoDarkMode-beton dark:text-icoDarkMode-wolf dark:hover:bg-icoDarkMode-deepBlack"
                            aria-label={t('commons:back')}>
                            <Icon icon="arrow-left" className="text-xs" />
                        </button>
                    )}
                    <Modal.Title>{albumInfo?.name}</Modal.Title>
                    <Badge variant="modal_campaign_type" iconSize="sm">
                        {t('content:modal.create_modal.action.manual')}
                    </Badge>
                </div>

                {!!albumInfo?.id && <InputAlbumName className="mb-6" />}

                <Modal.Content className="scrollbar flex-1 rounded-md">
                    <InstagramMediaType />

                    <h3 className="mt-5 font-GSemiBold text-xl font-semibold leading-[25px] tracking-[-0.5px] text-icoBlack dark:text-white">
                        {t('modal.compose_your_campaign')}
                    </h3>

                    <div className="pt-3 sm:pt-4">
                        <AlbumManualFilters />
                        <div className="space-y-3 rounded-md bg-icoGray-100 p-3 dark:bg-icoDarkMode-deepBlack sm:p-6">
                            {albumInfo?.criteria?.album_type === 'stories' ? (
                                <StoriesListWrapper />
                            ) : (
                                <PostListWrapper />
                            )}
                        </div>
                    </div>
                </Modal.Content>
            </Modal.Body>
            <FooterModal
                disableSubmitButton={!albumInfo?.medias?.length}
                submitStatus={submitStatus}
                submitAlbum={submitManualAlbum}
                onCancel={dismiss}
            />
        </div>
    );
};
